/**
 * FP3-035F Issue 2 — Shadow-preserving navigation hook.
 *
 * When an admin is shadowing a provider via `?as_provider={pid}`,
 * page-level navigation calls have historically stripped the query
 * param and dropped the admin back into their own context. This
 * hook wraps react-router's `useNavigate` so callers don't have to
 * thread the param through every link.
 *
 * Usage:
 *   const navigate = useShadowNavigate();
 *   navigate("/students/123");
 *   // becomes "/students/123?as_provider=<pid>" when in shadow mode.
 *
 * Callers passing their own query string are honored — we merge,
 * never overwrite caller-supplied params other than `as_provider`.
 */
import { useLocation, useNavigate } from "react-router-dom";


export default function useShadowNavigate() {
  const baseNavigate = useNavigate();
  const location = useLocation();

  return (path, opts) => {
    // Non-string paths (e.g. -1 for "go back") pass straight through.
    if (typeof path !== "string") {
      baseNavigate(path, opts);
      return;
    }
    const asProvider = new URLSearchParams(location.search || "").get(
      "as_provider",
    );
    if (!asProvider) {
      baseNavigate(path, opts);
      return;
    }
    const [pathOnly, existingQs = ""] = path.split("?");
    const qs = new URLSearchParams(existingQs);
    qs.set("as_provider", asProvider);
    baseNavigate(`${pathOnly}?${qs.toString()}`, opts);
  };
}
